import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../lib/siteContent";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { hydrate } = useAuth();
  const navigate = useNavigate();
  const site = useSiteContent();
  const headline = site.headline || "One platform for POS, inventory & your workforce.";
  const description = site.description || "Multi-tenant business management with real-time stock alerts, expiry tracking, selfie-verified attendance and BIR-compliant tax reporting.";
  const tags = site.tags?.length ? site.tags : ["Point of Sale", "Inventory", "Attendance", "Analytics"];
  const footer = site.footer || "Enterprise-grade tenant isolation · RBAC · Audit trails";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { identifier, password });
      const { data } = await api.get("/auth/me");
      hydrate(data);
      navigate("/home", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="flex items-center gap-2 relative z-10">
          <BrandLogo size="h-9 w-9" iconSize="h-5 w-5" light />
          <span className="font-heading font-bold text-xl tracking-tight">MatteusMx</span>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            {headline}
          </h1>
          <p className="text-slate-300 text-base leading-relaxed">
            {description}
          </p>
          <div className="flex gap-6 pt-4 text-sm text-slate-400">
            {tags.map((t) => <span key={t}>{t}</span>)}
          </div>
        </div>
        <p className="text-xs text-slate-500 relative z-10">{footer}</p>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-2">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <BrandLogo />
              <span className="font-heading font-bold text-lg">MatteusMx</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500">Use your username or email to access your workspace.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="identifier">Username or Email</Label>
              <Input
                id="identifier"
                data-testid="login-username-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="e.g. cashier.jane or jane@store.com"
                autoComplete="username"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="h-11 pr-10"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-toggle-password-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
              </div>
            </div>
            <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Sign in
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400">or</span></div>
          </div>

          <Button type="button" variant="outline" className="w-full h-11" onClick={googleLogin} data-testid="login-google-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-sm text-slate-500">
            New business?{" "}
            <Link to="/register" className="font-semibold text-slate-900 hover:underline" data-testid="register-business-link">
              Apply for an account
            </Link>
          </p>
          <p className="text-sm text-slate-500">
            Just shopping?{" "}
            <Link to="/" className="font-semibold text-slate-900 hover:underline" data-testid="browse-portal-link">
              Browse stores near you
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
